import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import PublishedWithChangesOutlinedIcon from "@mui/icons-material/PublishedWithChangesOutlined";

import { LojaPageContainer } from "layouts/pages/loja/common";
import MDTypography from "components/MDTypography";
import TableResultadosEventos from "./components/TableResultdosEventos";
import {
  getResultadosPendentes,
  getResultadosPublicados,
} from "services/resultadoCorrida";
import { IResultadoCorrida } from "interfaces/resultadoCorrida";

const ACCENT = "#F96030";

type ViewMode = "pendentes" | "publicados";

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="h4" fontWeight="bold" color={accent ? ACCENT : "text.primary"}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Resultados() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<IResultadoCorrida[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("pendentes");
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async (mode: ViewMode) => {
    setIsLoading(true);
    try {
      if (mode === "pendentes") {
        const { data: rows, status } = await getResultadosPendentes();
        if (status === 200) setData(rows);
      } else {
        const { data: rows, status } = await getResultadosPublicados("Publicado");
        if (status === 200) setData(rows);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(viewMode);
  }, [load, viewMode, refreshKey]);

  const handleRefresh = () => setRefreshKey(Date.now());

  const stats = useMemo(() => {
    const comWiclax = data.filter((e) => Boolean(e.resultado?.filenameWiclax)).length;
    const comPdf = data.filter((e) => (e.resultado?.filenamePdf?.length ?? 0) > 0).length;
    const comFotos = data.filter((e) => Boolean(e.resultado?.urllink?.trim())).length;
    return { total: data.length, comWiclax, comPdf, comFotos };
  }, [data]);

  const viewLabel =
    viewMode === "pendentes" ? "Aguardando resultados" : "Resultados publicados";

  return (
    <LojaPageContainer
      title="Resultados dos eventos"
      subtitle="Gerencie arquivos Wiclax, PDFs e links de fotos dos eventos finalizados."
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={viewMode === "pendentes" ? "Eventos pendentes" : "Eventos publicados"}
            value={stats.total}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Com Wiclax" value={stats.comWiclax} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Com PDF" value={stats.comPdf} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Com link de fotos" value={stats.comFotos} />
        </Grid>
      </Grid>

      <Box
        mb={2}
        p={2}
        sx={{
          borderRadius: 2,
          bgcolor: "grey.50",
          border: "1px solid",
          borderColor: "grey.200",
          borderLeft: `4px solid ${ACCENT}`,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <EmojiEventsOutlinedIcon sx={{ color: ACCENT }} />
            <MDTypography variant="button" fontWeight="medium" color="text">
              {viewLabel}
            </MDTypography>
          </Stack>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, next: ViewMode | null) => {
              if (next) setViewMode(next);
            }}
            sx={{
              flexShrink: 0,
              "& .MuiToggleButton-root": {
                px: 1.75,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                gap: 0.5,
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: ACCENT,
                  color: ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            <ToggleButton value="pendentes">
              <HourglassTopOutlinedIcon sx={{ fontSize: "1rem !important" }} />
              Aguardando
            </ToggleButton>
            <ToggleButton value="publicados">
              <PublishedWithChangesOutlinedIcon sx={{ fontSize: "1rem !important" }} />
              Publicados
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {!isLoading && data.length === 0 ? (
        <Box
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <EmojiEventsOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            {viewMode === "pendentes"
              ? "Nenhum evento aguardando resultado"
              : "Nenhum resultado publicado"}
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {viewMode === "pendentes"
              ? "Quando um evento encerrar sem resultado no site, ele aparecerá aqui."
              : "Alterne para “Aguardando” para publicar novos resultados."}
          </MDTypography>
        </Box>
      ) : (
        <TableResultadosEventos
          data={data}
          isLoading={isLoading}
          isUpdate={handleRefresh}
          viewMode={viewMode}
        />
      )}
    </LojaPageContainer>
  );
}
